// sdpUtils — pure SDP transforms. Kept out of sipService.ts so they can
// be unit-tested without importing JsSIP, the UA singleton, or the
// audioPageLogger side-effect chain.

/**
 * Strip `.local` (mDNS) ICE candidate lines from an SDP.
 *
 * BBB pattern #1 — Chrome 78+ anonymizes host candidates by sending a
 * `.local` mDNS hostname instead of an IP for the local network address.
 * That's fine when both peers are mDNS-capable on the same LAN — they
 * can resolve each other via multicast. For SIP-to-server flows like
 * ours (PSTN/FreeSWITCH backend), the server cannot resolve a browser's
 * mDNS name, so any mDNS candidate that wins ICE causes one-way audio
 * or no media at all.
 *
 * Stripping mDNS candidates from the OUTGOING offer/answer forces the
 * peer to pick from srflx / relay candidates, which are routable. This
 * is the audio-equivalent of "remove the path that can't possibly work".
 *
 * The regex matches the mDNS candidate format documented in RFC 6544 /
 * the WebRTC spec: `a=candidate:<foundation> <component> <transport>
 * <priority> <UUID>.local <port> typ host ...`. Idempotent — already-
 * stripped SDPs pass through unchanged.
 *
 * Source: https://github.com/bigbluebutton/bigbluebutton/blob/main/.../sdpUtils.js
 */
const MDNS_CANDIDATE_RE = /^a=candidate:\S+ \S+ \S+ \S+ \S+\.local \S+[^\r\n]*\r?\n/gm;

export function stripMdnsCandidates(sdp: string): string {
    const matches = sdp.match(MDNS_CANDIDATE_RE);
    if (!matches || matches.length === 0) return sdp;
    return sdp.replace(MDNS_CANDIDATE_RE, '');
}

export function countMdnsCandidates(sdp: string): number {
    return (sdp.match(MDNS_CANDIDATE_RE) ?? []).length;
}
